// Capa API del "Perfil de persona" del CRM (leads de la misma persona +
// relacionados/familiares) consumida por el panel LeadPersonProfile de la ficha.
// Thin wrappers sobre crmRequest, espejo de leadDetailApi.getPerson /
// .personAction / .searchLeads del CRM (apps/web/src/features/lead-detail/api.ts).
//
// Los endpoints aceptan leadId hex-32 O Crockford-26 en input, pero DEVUELVEN
// los `leadId` de los miembros en hex-32 interno (a diferencia de /leads, que
// serializa a Crockford). Normalizamos aquí a canónico — regla de la capa API —
// para que las vistas comparen contra el leadUlid de la ruta y linken a
// /leads/:leadUlid sin dar 400.

import { crmRequest } from "./crm";
import { canonicalLeadId, canonicalLeadIdOrNull } from "./crmLeadId";

export type PersonMemberKind = "same_person" | "related";
export type PersonMemberSource =
  | "none_chain"
  | "reset_chain"
  | "dni"
  | "plus_tag"
  | "merge_ledger"
  | "phone"
  | "manual";
export type PersonMemberStatus = "auto" | "confirmed" | "rejected";

export type PersonMemberView = {
  /** Canonicalizado (Crockford-26) por esta capa; el CRM lo emite en hex-32. */
  leadId: string;
  kind: PersonMemberKind;
  source: PersonMemberSource;
  status: PersonMemberStatus;
  email: string | null;
  fullName: string | null;
  market: string | null;
  leadStatus: string | null;
  flowState: string | null;
  hasSale: boolean;
  isNoned: boolean;
  /** Fusionado en otro lead: sin fila en projection, email/nombre del snapshot. */
  isMergedAway: boolean;
  createdAt: string | null;
  isPrimary: boolean;
};

export type PersonProfileView = {
  personId: string;
  primaryLead: string;
  members: PersonMemberView[];
};

export type PersonActionInput = {
  action: "confirm" | "reject" | "promote" | "attach";
  memberLeadId: string;
  kind?: PersonMemberKind;
};

// Subconjunto del SearchResult del CRM que necesita el picker de "vincular
// otro lead" (GET /search). `id` canonicalizado por esta capa.
export type PersonSearchLead = {
  id: string;
  fullName: string | null;
  email: string | null;
  phone: string | null;
  market: string | null;
};

type RawSearchResponse = {
  results: Array<{
    lead: {
      id: string;
      fullName?: string | null;
      email?: string | null;
      phone?: string | null;
      market?: string | null;
    };
  }>;
  truncated: boolean;
};

function normalizeProfile(p: PersonProfileView): PersonProfileView {
  return {
    ...p,
    primaryLead: canonicalLeadId(p.primaryLead),
    members: p.members.map((m) => ({
      ...m,
      leadId: canonicalLeadId(m.leadId),
    })),
  };
}

// GET /leads/:id/person -> { profile | null }. null = el lead no pertenece a
// ningún perfil de persona (caso mayoritario), NO es un error.
export function getPersonProfile(
  leadId: string,
): Promise<PersonProfileView | null> {
  return crmRequest<{ profile: PersonProfileView | null }>(
    `/leads/${encodeURIComponent(leadId)}/person`,
  ).then((res) => (res.profile ? normalizeProfile(res.profile) : null));
}

// POST /leads/:id/person/actions -> { profile } (re-hidratado tras la acción).
//   - attach: crea el perfil si el ancla no tiene (y adjunta memberLeadId con
//     kind, default 'related', como manual/confirmed). Re-attach de un
//     rejected = restaurar.
//   - confirm/reject: curación de una sugerencia; promote: related → misma
//     persona.
// Errores con código en el body: 400 person_attach_self, 409
// person_member_elsewhere (el lead ya está en OTRO perfil; hay que rechazarlo
// allí primero), 404 person_member_not_found.
export function personAction(
  leadId: string,
  input: PersonActionInput,
): Promise<PersonProfileView> {
  return crmRequest<{ profile: PersonProfileView }>(
    `/leads/${encodeURIComponent(leadId)}/person/actions`,
    { method: "POST", body: input },
  ).then((res) => normalizeProfile(res.profile));
}

// GET /search?q=&limit= — búsqueda ligera de leads para el picker de "vincular
// otro lead" (misma que usa el AddRelatedForm del CRM).
export function searchPersonLeads(
  q: string,
  limit = 5,
): Promise<PersonSearchLead[]> {
  const params = new URLSearchParams({ q, limit: String(limit) });
  return crmRequest<RawSearchResponse>(`/search?${params.toString()}`).then(
    (res) =>
      res.results.map(({ lead }) => ({
        id: canonicalLeadIdOrNull(lead.id) ?? lead.id,
        fullName: lead.fullName ?? null,
        email: lead.email ?? null,
        phone: lead.phone ?? null,
        market: lead.market ?? null,
      })),
  );
}
